/**
 * ClinicalProceduresCatalog — Admin Settings Page
 * Manage the clinic's injectable drugs and CLIA-waived lab list.
 * Admin can add, edit, deactivate. AI looks up billing codes for new items.
 */
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Edit3, Loader2, Check, Syringe, FlaskConical, ShieldCheck, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const CATEGORY_CONFIG = {
  injection: { label: 'Injection', color: 'bg-purple-100 text-purple-700', icon: Syringe },
  vaccine:   { label: 'Vaccine',   color: 'bg-teal-100 text-teal-700',   icon: ShieldCheck },
  clia_lab:  { label: 'CLIA Lab',  color: 'bg-blue-100 text-blue-700',   icon: FlaskConical },
};

export default function ClinicalProceduresCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/procedures/catalog?active_only=false');
      setItems(res.data.procedures || []);
    } catch { toast.error('Failed to load catalog'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id) => {
    await api.delete(`/procedures/catalog/${id}`);
    toast.success('Procedure deactivated');
    load();
  };

  const handleActivate = async (id) => {
    await api.put(`/procedures/catalog/${id}`, { active: true });
    toast.success('Procedure activated');
    load();
  };

  const filtered = items.filter(item => {
    const matchesSearch = !search || item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.billing_code?.includes(search);
    const matchesCat = !categoryFilter || item.category === categoryFilter;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-slate-800">Clinic Procedures Catalog</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Injections, vaccines, and CLIA-waived in-office labs. Codes auto-drop to billing when nurse documents.
          </p>
        </div>
        <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setShowAdd(true)}>
          <Plus className="w-3.5 h-3.5" /> Add Procedure
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name or code..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all
              ${categoryFilter === key ? cfg.color + ' ring-1 ring-current' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
            {cfg.label}
          </button>
        ))}
        <span className="text-[10px] text-slate-400 ml-auto">{filtered.length} items</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Category</th>
                <th className="text-left px-3 py-2.5 font-semibold">Code</th>
                <th className="text-left px-3 py-2.5 font-semibold">Default Dose</th>
                <th className="text-left px-3 py-2.5 font-semibold">Routes</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const cfg = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.injection;
                const CatIcon = cfg.icon;
                return (
                  <tr key={item.id} className={`border-t border-slate-50 ${!item.active ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{item.name}</td>
                    <td className="px-3 py-2.5">
                      <span className={`flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color}`}>
                        <CatIcon className="w-3 h-3" />{cfg.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono font-bold text-slate-700">{item.billing_code}</td>
                    <td className="px-3 py-2.5 text-slate-600">{item.default_dose || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-500">{(item.routes || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium
                        ${item.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                        {item.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 flex items-center gap-1.5">
                      <button onClick={() => setEditItem(item)}
                        className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      {item.active ? (
                        <button onClick={() => handleDeactivate(item.id)}
                          className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => handleActivate(item.id)}
                          className="p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(showAdd || editItem) && (
        <AddProcedureModal
          item={editItem}
          onClose={() => { setShowAdd(false); setEditItem(null); }}
          onSaved={() => { setShowAdd(false); setEditItem(null); load(); }}
        />
      )}
    </div>
  );
}


function AddProcedureModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [name, setName]         = useState(item?.name || '');
  const [category, setCategory] = useState(item?.category || 'injection');
  const [code, setCode]         = useState(item?.billing_code || '');
  const [codeType, setCodeType] = useState(item?.billing_code_type || 'HCPCS');
  const [dose, setDose]         = useState(item?.default_dose || '');
  const [units, setUnits]       = useState(item?.default_units || 1);
  const [routes, setRoutes]     = useState((item?.routes || []).join(', '));
  const [description, setDesc]  = useState(item?.description || '');
  const [lookingUp, setLooking] = useState(false);
  const [saving, setSaving]     = useState(false);

  const lookupCode = async () => {
    if (!name) return;
    setLooking(true);
    try {
      const res = await api.post('/procedures/catalog/lookup-code', { name });
      if (res.data.code) {
        setCode(res.data.code);
        setCodeType(res.data.code_type || 'CPT');
        if (res.data.description) setDesc(res.data.description);
        toast.success(`Found: ${res.data.code} — ${res.data.description}`);
      }
    } catch { toast.error('Code lookup failed'); }
    finally { setLooking(false); }
  };

  const handleSave = async () => {
    if (!name || !code) { toast.error('Name and billing code are required'); return; }
    setSaving(true);
    try {
      const payload = {
        name, category, billing_code: code, billing_code_type: codeType,
        default_dose: dose, default_units: units,
        routes: routes.split(',').map(r => r.trim()).filter(Boolean),
        description, keywords: [name.toLowerCase()],
      };
      if (isEdit) {
        await api.put(`/procedures/catalog/${item.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/procedures/catalog', payload);
        toast.success('Added to catalog');
      }
      onSaved();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[500px] overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">
            {isEdit ? 'Edit Procedure' : 'Add New Procedure'}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Procedure Name *</label>
            <div className="flex gap-2 mt-0.5">
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Clindamycin 600mg IM"
                className="flex-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" />
              <button onClick={lookupCode} disabled={lookingUp || !name}
                className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[11px] font-medium disabled:opacity-40 whitespace-nowrap flex items-center gap-1">
                {lookingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                Find Code
              </button>
            </div>
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none bg-white">
              <option value="injection">Injection</option>
              <option value="vaccine">Vaccine</option>
              <option value="clia_lab">CLIA Lab</option>
            </select>
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Billing Code *</label>
            <div className="flex gap-1 mt-0.5">
              <input value={code} onChange={e => setCode(e.target.value)} placeholder="J0696"
                className="flex-1 px-2.5 py-1.5 text-xs font-mono border border-slate-200 rounded-lg focus:outline-none" />
              <select value={codeType} onChange={e => setCodeType(e.target.value)}
                className="w-20 px-1.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none bg-white">
                <option>HCPCS</option>
                <option>CPT</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Default Dose</label>
            <input value={dose} onChange={e => setDose(e.target.value)} placeholder="500mg"
              className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Routes (comma separated)</label>
            <input value={routes} onChange={e => setRoutes(e.target.value)} placeholder="IM, SubQ"
              className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
          </div>
          <div className="col-span-2">
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Description</label>
            <input value={description} onChange={e => setDesc(e.target.value)}
              placeholder="Brief description"
              className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600">Cancel</button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs h-8 gap-1">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            {isEdit ? 'Update' : 'Add to Catalog'}
          </Button>
        </div>
      </div>
    </div>
  );
}
